import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../components/base/Modal";
import { Button, notifyError, cn } from "../../../../components/ui";
import { usePatrolsStore } from "../../patrolsStore";
import { apiErrorMessage } from "../../services/basesApi";
import { ROUND_STATUSES, type PatrolRound, type RoundCheck } from "../../services/patrolsApi";
import { DetailDrawer, FilterSelect, FormField, Kpi, LoadBlock, PersonAvatar, ProgressBar, SearchBox, SiteTree, Toolbar, inputCls, pctTone, shortDateTime, timeOnly } from "../../components/console";
import { METHOD_ICONS, RoundBadge, treeMatch, useRondinesStatus, useRouteTree } from "./shared";

// Rondas / bitácora: hora planeada vs. real, retraso, guardia, puntos
// cumplidos u omitidos y % de cumplimiento (calculado por el servidor).
// Desde el drawer se marcan puntos y se cierra la ronda.

const CHECK_ICON: Record<RoundCheck["status"], string> = { done: "ri-checkbox-circle-fill text-success", skipped: "ri-close-circle-fill text-error", pending: "ri-time-line text-content-muted" };

export default function RondinesRounds() {
  const { t, i18n } = useTranslation("bases");
  const { rounds, routes, schedules, saveRound } = usePatrolsStore();
  const { status, error, retry } = useRondinesStatus();
  const tree = useRouteTree();
  const [sel, setSel] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [st, setSt] = useState<string>("");
  const [day, setDay] = useState<string>("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState<{ route_id: string; guard: string; plannedStart: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const open = rounds.find((r) => r.id === openId) ?? null;
  const lng = i18n.language;

  const days = useMemo(() => [...new Set(rounds.map((r) => new Date(Date.parse(r.plannedStart) - 6 * 3600 * 1000).toISOString().slice(0, 10)))].sort().reverse(), [rounds]);
  const list = rounds
    .filter((r) => treeMatch(sel, r) && (!st || r.status === st))
    .filter((r) => !day || new Date(Date.parse(r.plannedStart) - 6 * 3600 * 1000).toISOString().slice(0, 10) === day)
    .filter((r) => !q || `${r.routeName} ${r.guard?.name}`.toLowerCase().includes(q.toLowerCase()));
  const closed = list.filter((r) => ["completed", "partial", "missed"].includes(r.status));
  const avg = closed.length ? Math.round(closed.reduce((a, r) => a + r.compliancePct, 0) / closed.length) : null;
  const delays = closed.filter((r) => typeof r.delayMinutes === "number");
  const avgDelay = delays.length ? Math.round(delays.reduce((a, r) => a + (r.delayMinutes ?? 0), 0) / delays.length) : null;

  const update = async (r: PatrolRound, body: Record<string, unknown>) => {
    setBusy(true);
    try {
      await saveRound(r.id, body);
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    } finally {
      setBusy(false);
    }
  };

  const guardsFor = (routeId: string) => {
    const map = new Map<string, { id: string; name: string }>();
    schedules.filter((s) => s.route_id === routeId).forEach((s) => s.guards.forEach((g) => map.set(g.id || g.name, g)));
    return [...map.values()];
  };

  const create = async () => {
    if (!creating?.route_id) return;
    setBusy(true);
    try {
      const guard = guardsFor(creating.route_id).find((g) => g.id === creating.guard || g.name === creating.guard) ?? (creating.guard ? { id: "", name: creating.guard } : null);
      const saved = await saveRound(null, { route_id: creating.route_id, guard, plannedStart: new Date(creating.plannedStart).toISOString(), actualStart: new Date().toISOString() });
      setCreating(null);
      setOpenId(saved.id);
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    } finally {
      setBusy(false);
    }
  };

  const localNow = () => {
    const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
    return d.toISOString().slice(0, 16);
  };

  return (
    <div className="flex h-full w-full overflow-hidden">
      <SiteTree title={t("rondines.treeTitle")} allLabel={t("rondines.allRoutes")} nodes={tree} selected={sel} onSelect={setSel} />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Toolbar>
          <div className="mr-2 flex items-center gap-2">
            <i className="ri-file-list-3-line text-lg text-primary-500" />
            <h1 className="text-sm font-semibold text-content">{t("rondines.rounds.title")}</h1>
          </div>
          <SearchBox value={q} onChange={setQ} placeholder={t("rondines.rounds.search")} />
          <FilterSelect label={t("fields.status")} value={st} onChange={setSt} options={ROUND_STATUSES.map((s) => ({ value: s, label: t(`rondines.roundStatus.${s}`) }))} />
          <FilterSelect label={t("rondines.rounds.day")} value={day} onChange={setDay} options={days.map((d) => ({ value: d, label: new Date(`${d}T12:00:00`).toLocaleDateString(lng.startsWith("en") ? "en-US" : "es-MX", { weekday: "short", day: "numeric", month: "short" }) }))} />
          <div className="ml-auto">
            <Button size="sm" icon={<i className="ri-play-circle-line" />} disabled={!routes.length} onClick={() => setCreating({ route_id: routes[0]?.id ?? "", guard: "", plannedStart: localNow() })}>
              {t("rondines.rounds.start")}
            </Button>
          </div>
        </Toolbar>
        <div className="min-w-0 flex-1 overflow-y-auto p-4">
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            <Kpi icon="ri-file-list-3-line" label={t("rondines.rounds.count")} value={list.length} />
            <Kpi icon="ri-checkbox-circle-line" label={t("rondines.rounds.compliance")} value={avg === null ? "—" : `${avg}%`} progress={avg} tone={pctTone(avg)} />
            <Kpi icon="ri-check-double-line" label={t("rondines.roundStatus.completed")} value={list.filter((r) => r.status === "completed").length} tone="success" />
            <Kpi icon="ri-close-circle-line" label={t("rondines.roundStatus.missed")} value={list.filter((r) => r.status === "missed").length} tone="error" hint={t("rondines.kpi.partialN", { n: list.filter((r) => r.status === "partial").length })} />
            <Kpi icon="ri-timer-line" label={t("rondines.rounds.avgDelay")} value={avgDelay === null ? "—" : `${avgDelay} min`} tone={avgDelay !== null && avgDelay > 10 ? "warning" : "info"} />
          </div>
          <LoadBlock status={status} error={error} onRetry={retry} empty={rounds.length === 0} emptyIcon="ri-file-list-3-line" emptyTitle={t("rondines.rounds.emptyTitle")} emptyText={t("rondines.rounds.emptyText")}>
            <div className="overflow-x-auto rounded-xl border border-line bg-card">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-subtle text-left text-xs uppercase tracking-wide text-content-secondary">
                  <tr>
                    <th className="px-3 py-2">{t("rondines.rounds.planned")}</th>
                    <th className="px-3 py-2">{t("rondines.rounds.actual")}</th>
                    <th className="px-3 py-2">{t("fields.route")}</th>
                    <th className="px-3 py-2">{t("rondines.rounds.guard")}</th>
                    <th className="px-3 py-2">{t("rondines.rounds.points")}</th>
                    <th className="px-3 py-2">{t("rondines.rounds.compliance")}</th>
                    <th className="px-3 py-2">{t("fields.status")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {list.slice(0, 300).map((r) => (
                    <tr key={r.id} onClick={() => setOpenId(r.id)} className={cn("cursor-pointer hover:bg-subtle", openId === r.id && "bg-primary-500/5")}>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-content">{shortDateTime(r.plannedStart, lng)}</td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-content-secondary">
                        {r.actualStart ? timeOnly(r.actualStart, lng) : "—"}
                        {typeof r.delayMinutes === "number" && r.delayMinutes !== 0 ? <span className={cn("block text-[11px]", r.delayMinutes > 10 ? "text-warning" : "text-content-muted")}>({r.delayMinutes > 0 ? "+" : ""}{r.delayMinutes} min)</span> : null}
                      </td>
                      <td className="min-w-[150px] px-3 py-2 text-content">{r.routeName}</td>
                      <td className="px-3 py-2">
                        <span className="flex max-w-[150px] items-center gap-2 text-content-secondary">
                          <PersonAvatar name={r.guard?.name ?? "?"} size="sm" /> <span className="truncate" title={r.guard?.name ?? undefined}>{r.guard?.name ?? "—"}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2 tabular-nums text-content-secondary">
                        {r.doneCheckpoints}/{r.totalCheckpoints}
                        {r.skippedCheckpoints ? <span className="ml-1 text-[11px] text-error">−{r.skippedCheckpoints}</span> : null}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <ProgressBar pct={r.compliancePct} tone={pctTone(r.compliancePct)} className="w-14" />
                          <span className="text-xs tabular-nums text-content-secondary">{r.compliancePct}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <RoundBadge status={r.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {list.length === 0 ? <p className="py-8 text-center text-sm text-content-muted">{t("list.noMatches")}</p> : null}
            </div>
          </LoadBlock>
        </div>
      </div>

      <DetailDrawer
        open={Boolean(open)}
        onClose={() => setOpenId(null)}
        title={open?.routeName}
        subtitle={open ? `${shortDateTime(open.plannedStart, lng)} · ${open.guard?.name ?? ""}` : null}
        actions={open && (open.status === "in_progress" || open.status === "scheduled") ? (
          <Button size="sm" loading={busy} icon={<i className="ri-flag-line" />} onClick={() => void update(open, { finished: true })}>
            {t("rondines.rounds.finish")}
          </Button>
        ) : null}
      >
        {open ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <Kpi icon="ri-checkbox-circle-line" label={t("rondines.rounds.compliance")} value={`${open.compliancePct}%`} progress={open.compliancePct} tone={pctTone(open.compliancePct)} />
              <Kpi icon="ri-timer-line" label={t("rondines.rounds.delay")} value={typeof open.delayMinutes === "number" ? `${open.delayMinutes} min` : "—"} tone="info" />
              <Kpi icon="ri-hourglass-line" label={t("rondines.rounds.duration")} value={typeof open.durationMinutes === "number" ? `${open.durationMinutes} min` : "—"} tone="primary" />
            </div>
            <div className="flex items-center justify-between text-xs text-content-secondary">
              <span>{t("rondines.rounds.planned")}: {timeOnly(open.plannedStart, lng)} – {timeOnly(open.plannedEnd, lng)}</span>
              <RoundBadge status={open.status} />
            </div>
            <ol className="relative space-y-3 border-l border-line pl-5">
              {open.checkpoints.map((c) => (
                <li key={c.checkpoint_id} className="relative">
                  <i className={cn("absolute -left-[27px] top-0.5 bg-surface text-base", CHECK_ICON[c.status])} />
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-content">
                        {c.order}. {c.name}
                      </div>
                      <div className="text-[11px] text-content-muted">
                        {c.method ? <i className={METHOD_ICONS[c.method]} /> : null} {c.status === "done" ? timeOnly(c.at, lng) : t(`rondines.checkStatus.${c.status}`)}
                        {c.note ? ` · ${c.note}` : ""}
                      </div>
                    </div>
                    {open.status === "in_progress" || open.status === "scheduled" ? (
                      <span className="flex shrink-0 gap-1">
                        <Button size="xs" variant="successSoft" disabled={busy || c.status === "done"} onClick={() => void update(open, { actualStart: open.actualStart || new Date().toISOString(), checkpoints: [{ checkpoint_id: c.checkpoint_id, status: "done" }] })}>
                          {t("rondines.rounds.markDone")}
                        </Button>
                        <Button size="xs" variant="dangerSoft" disabled={busy || c.status === "skipped"} onClick={() => void update(open, { checkpoints: [{ checkpoint_id: c.checkpoint_id, status: "skipped" }] })}>
                          {t("rondines.rounds.markSkipped")}
                        </Button>
                      </span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
            {open.notes ? <p className="rounded-lg bg-subtle p-3 text-xs text-content-secondary">{open.notes}</p> : null}
          </div>
        ) : null}
      </DetailDrawer>

      {creating ? (
        <Modal
          title={t("rondines.rounds.start")}
          onClose={() => setCreating(null)}
          content={
            <div className="grid grid-cols-1 gap-3">
              <FormField label={t("fields.route")}>
                <select className={inputCls} value={creating.route_id} onChange={(e) => setCreating({ ...creating, route_id: e.target.value, guard: "" })}>
                  {routes.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("rondines.rounds.guard")}>
                <input className={inputCls} list="round-guards" value={creating.guard} onChange={(e) => setCreating({ ...creating, guard: e.target.value })} />
                <datalist id="round-guards">
                  {guardsFor(creating.route_id).map((g) => (
                    <option key={g.id || g.name} value={g.name} />
                  ))}
                </datalist>
              </FormField>
              <FormField label={t("rondines.rounds.planned")}>
                <input className={inputCls} type="datetime-local" value={creating.plannedStart} onChange={(e) => setCreating({ ...creating, plannedStart: e.target.value })} />
              </FormField>
              <div className="flex justify-end gap-2 border-t border-line pt-4">
                <Button variant="secondary" onClick={() => setCreating(null)}>
                  {t("common.cancel")}
                </Button>
                <Button loading={busy} onClick={() => void create()} icon={<i className="ri-play-circle-line" />}>
                  {t("rondines.rounds.startShort")}
                </Button>
              </div>
            </div>
          }
        />
      ) : null}
    </div>
  );
}
